import { apiClient, attendanceQueryKey } from '@app/lib'
import { type SugarAttendanceResponse, type SugarAttendanceUpsert } from '@app/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

// Check if a response with success: true is actually a rejection where nothing was written.
// Keyed on sheet.attendanceGated, NOT on the presence of an error message: a partial-processing
// response also carries a type: 'error' message (e.g. "Enrollment is read-only") alongside a
// warning, yet it did write entries. Resetting the form there is correct; skipping it would
// strand the provider with a permanently dirty, unsaveable form.
export const isAttendanceSaveRejected = ({ data }: SugarAttendanceResponse) => data.sheet.attendanceGated === true

// Exported so the header can disable its site picker while a save is in flight.
export const updateAttendanceMutationKey = ['updateAttendance']

export const useUpdateAttendance = () => {
  const queryClient = useQueryClient()
  const { isPending, mutateAsync } = useMutation({
    mutationKey: updateAttendanceMutationKey,
    mutationFn: async (input: SugarAttendanceUpsert) => {
      const { data } = await apiClient.post<SugarAttendanceResponse>('/api/attendance', input)

      return data
    },
    onSuccess: async (data, { periodId, programYearId, siteId }) => {
      if (isAttendanceSaveRejected(data)) {
        await queryClient.invalidateQueries({
          queryKey: attendanceQueryKey({ periodId, programYearId, siteId })
        })
      } else {
        queryClient.setQueryData(attendanceQueryKey({ periodId, programYearId, siteId }), data)
      }
    }
  })

  return {
    isUpdatingAttendance: isPending,
    updateAttendance: mutateAsync
  }
}
